/**
 * 검색어 매칭 우선순위. 숫자가 작을수록 검색 결과 상단에 두기 적합한 매칭이다.
 * 정렬 시 오름차순으로 사용한다.
 */
export const SEARCH_TEXT_MATCH_PRIORITY = {
  EXACT: 0,
  STARTS_WITH: 1,
  INCLUDES: 2,
  NO_MATCH: Number.POSITIVE_INFINITY,
} as const;

/**
 * 단일 문자열 필드에 대한 검색 정렬 메타. `tier`가 같으면 `matchIndex`가 작을수록 검색어가 문자열 앞쪽에서 매칭된 것이다.
 */
export type SearchTextMatchSortMeta = {
  matchIndex: number;
  tier: number;
};

/**
 * `tier` 비교 후 같은 tier면 `matchIndex`로 비교한다.
 * @description
 * - 둘 다 비매칭(`tier`가 유한하지 않음)이면 `Infinity - Infinity`로 `NaN`이 나지 않도록 0(동점)을 반환한다.
 */
export function compareSearchTextMatchSortMeta(
  a: SearchTextMatchSortMeta,
  b: SearchTextMatchSortMeta,
): number {
  const aBad = !Number.isFinite(a.tier);
  const bBad = !Number.isFinite(b.tier);
  if (aBad && bBad) return 0;
  if (a.tier !== b.tier) return a.tier - b.tier;
  return a.matchIndex - b.matchIndex;
}

export type SearchTextMatchPriorityOptions = {
  /**
   * 비교 시 대소문자 무시. 기본값 true.
   */
  ignoreCase?: boolean;
  /**
   * 검색어 앞뒤 공백 제거 후 비교. 기본값 true.
   */
  trimKeyword?: boolean;
  /**
   * 비교 대상 문자열 앞뒤 공백 제거 후 비교. 기본값 true.
   */
  trimText?: boolean;
};

const normalize = (
  value: string,
  options: Pick<SearchTextMatchPriorityOptions, 'ignoreCase' | 'trimKeyword' | 'trimText'> & {
    role: 'keyword' | 'text';
  },
): string => {
  const trim =
    options.role === 'keyword' ? options.trimKeyword !== false : options.trimText !== false;
  let result = trim ? value.trim() : value;
  if (options.ignoreCase !== false) {
    result = result.toLowerCase();
  }
  return result;
};

/**
 * `getSearchTextMatchPriority`와 동일한 키워드 정규화 기준으로, 비교·정렬에서 검색을 건너뛸지 판단한다.
 */
export function isSearchKeywordEmpty(
  searchKeyword: string,
  options?: Pick<SearchTextMatchPriorityOptions, 'trimKeyword'>,
): boolean {
  const kw = normalize(searchKeyword, { ...options, role: 'keyword' });
  return kw === '';
}

/**
 * 단일 문자열에 대해 검색 매칭 tier와, `includes`일 때 정규화 문자열 기준 `indexOf` 위치를 반환한다.
 * @description
 * - `exact`·`startsWith`는 `matchIndex` 0.
 * - `includes`는 정규화된 대상 문자열에서 검색어가 시작하는 인덱스.
 * - 비매칭은 tier·`matchIndex` 모두 무한대에 가깝게 둔다.
 */
export function getSearchTextMatchSortMeta(
  text: string,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): SearchTextMatchSortMeta {
  const kw = normalize(searchKeyword, { ...options, role: 'keyword' });
  if (kw === '') {
    return {
      tier: SEARCH_TEXT_MATCH_PRIORITY.NO_MATCH,
      matchIndex: Number.POSITIVE_INFINITY,
    };
  }

  const normalizedText = normalize(text, { ...options, role: 'text' });

  if (normalizedText === kw) {
    return { tier: SEARCH_TEXT_MATCH_PRIORITY.EXACT, matchIndex: 0 };
  }
  if (normalizedText.startsWith(kw)) {
    return { tier: SEARCH_TEXT_MATCH_PRIORITY.STARTS_WITH, matchIndex: 0 };
  }
  const matchIndex = normalizedText.indexOf(kw);
  if (matchIndex !== -1) {
    return { tier: SEARCH_TEXT_MATCH_PRIORITY.INCLUDES, matchIndex };
  }

  return {
    tier: SEARCH_TEXT_MATCH_PRIORITY.NO_MATCH,
    matchIndex: Number.POSITIVE_INFINITY,
  };
}

/**
 * `getSearchTextMatchSortMeta`와 동일한 정규화 기준으로, 원문에서 첫 매칭 구간의 `[start, end)` 인덱스를 반환한다.
 * @description
 * - 정렬·필터와 같은 부분 문자열(`indexOf`) 매칭을 전제로 한다.
 * - `trimText`가 켜져 있으면 `normalize(origin)`과 같은 트림된 본문 좌표를 `originText` 기준으로 보정한다.
 */
export function getSearchTextMatchHighlightSpan(
  originText: string,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): { end: number; start: number } | null {
  const kw = normalize(searchKeyword, { ...options, role: 'keyword' });
  if (kw === '') return null;

  const normalizedText = normalize(originText, { ...options, role: 'text' });
  const idx = normalizedText.indexOf(kw);
  if (idx === -1) return null;

  const trimText = options?.trimText !== false;
  const lead = trimText ? originText.search(/\S/) : 0;
  if (trimText && lead === -1) return null;

  const start = lead + idx;
  const end = start + kw.length;
  return { start, end };
}

/**
 * 단일 문자열 필드에 대해 검색어와의 매칭 우선순위를 반환한다.
 * @description
 * - 우선순위: 전체 일치(0) → 접두 일치(1) → 부분 포함(2) → 비매칭(∞)
 * - 검색어가 빈 문자열이면 `NO_MATCH`이다.
 */
export function getSearchTextMatchPriority<T>(
  item: T,
  getSearchText: (item: T) => string,
  searchKeyword: string,
  options?: SearchTextMatchPriorityOptions,
): number {
  return getSearchTextMatchSortMeta(getSearchText(item), searchKeyword, options).tier;
}
